import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import type { PanelContentProps } from '@/src/components/ui/PanelSystem';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { panelBody, PanelHeader } from '@/src/components/ui/PanelSystem';
import { getEmbeddedV3Costs } from '@/src/lib/utils/v3ListingDetail';
import { LeaseTermsInline } from '../LeaseTermsInline';
import { EstimateCostSection } from '../EstimateCostSection';
import { getEffectiveCostsAndFees } from '../costSelection';
import { type CostAndFeesCalculatorState, useCostAndFeesCalculatorState } from '../useCostAndFeesCalculatorState';
import { UnitPicker } from './UnitPicker';
import { disclaimer } from './costAndFeesSpoke.styles';

type CostAndFeesSpokeContentProps = PanelContentProps & {
  showHeader?: boolean;
  bodyClassName?: string;
  collapseFeeBreakdown?: boolean;
  showEstimateSpecialOffer?: boolean;
  showEstimateSummary?: boolean;
  calculatorState?: CostAndFeesCalculatorState;
};

export function CostAndFeesSpokeContent({
  panelProps,
  showHeader = true,
  bodyClassName,
  collapseFeeBreakdown = false,
  showEstimateSpecialOffer = true,
  showEstimateSummary = true,
  calculatorState,
}: CostAndFeesSpokeContentProps) {
  const t = useTranslations('hdp');
  const listing = panelProps?.listing as PropertyDetailListing | undefined;
  const hasSpecialOffers = listing?.hasSpecialOffers;
  const specialOffers = listing?.specialOffers;

  const costsData = getEmbeddedV3Costs(listing);

  const internalCalculatorState = useCostAndFeesCalculatorState();
  const effectiveCalculatorState = calculatorState ?? internalCalculatorState;
  const { selectedBedCount, selectedUnitZpid } = effectiveCalculatorState;

  const { effectiveFees, effectiveCosts } = useMemo(() => {
    if (!costsData) return { effectiveFees: null, effectiveCosts: null };
    return getEffectiveCostsAndFees(costsData, { selectedBedCount, selectedUnitZpid });
  }, [costsData, selectedBedCount, selectedUnitZpid]);

  return (
    <>
      {showHeader && <PanelHeader title={t('costsAndFees')} />}
      <div className={cx(panelBody, bodyClassName)}>
        {costsData && (
          <UnitPicker
            costsData={costsData}
            selectedBedCount={selectedBedCount}
            onBedChange={effectiveCalculatorState.setSelectedBedCount}
            selectedUnitZpid={selectedUnitZpid}
            onUnitChange={effectiveCalculatorState.setSelectedUnitZpid}
          />
        )}

        <EstimateCostSection
          effectiveCosts={effectiveCosts}
          effectiveFees={effectiveFees}
          hasSpecialOffers={hasSpecialOffers}
          specialOfferText={specialOffers?.[0]?.description}
          collapseFeeBreakdown={collapseFeeBreakdown}
          showSpecialOffer={showEstimateSpecialOffer}
          showSummary={showEstimateSummary}
          selectedFeeOptions={effectiveCalculatorState.selectedFeeOptions}
          onSelectedFeeOptionChange={effectiveCalculatorState.updateSelectedFeeOption}
        />

        {listing && <LeaseTermsInline listing={listing} />}

        <p className={disclaimer}>{t('feesDisclaimer')}</p>
      </div>
    </>
  );
}
